import {
  DatasetDataApiRequestData,
  SubsetDataApiRequestData,
} from '../../api/types/types';
import { requestDatasetData } from '../../api/requests/dataset/requestDatasetData';
import { apiRequest } from '../../api/utils/apiRequest';
import { detailedApiMethodFetcher } from '../../api/utils/fetchers/detailedApiMethodFetcher';
import { X_CACHEDQUERY_ID } from '../../constants';
import { Dataset } from '../../types/types';
import { getDataParamsString } from '../apiRequests/getDataParamStrings';
import { resolveTemplate } from '../json/resolveTemplate';

type FetchRowIdsParams = {
  dataset: Dataset;
  requestData: DatasetDataApiRequestData | SubsetDataApiRequestData;
  resolvedFilters: Record<string, unknown>;
  resolvedSorting: any[];
  idItem: string;
  sessionToken: string | null;
  // Already request-shaped filter merged on top of the converted resolvedFilters
  // in the string-apiMethod flow (detailed flow gets it via requestData, built
  // in buildLocateRequestData). Keeps the probe scoped like the grid fetch.
  additionalFilterObject?: Record<string, any>;
};

export type FetchRowIdsResult = {
  ids: any[];
  cachedQueryId: string | undefined;
};

// ID-only probe used by locate-by-id flow. Returns null on transport failure.
export const fetchRowIds = async ({
  dataset,
  requestData,
  resolvedFilters,
  resolvedSorting,
  idItem,
  sessionToken,
  additionalFilterObject,
}: FetchRowIdsParams): Promise<FetchRowIdsResult | null> => {
  try {
    let apiRes;
    if (typeof dataset.apiMethod === 'string') {
      const { apiUrl } = await resolveTemplate(
        { apiUrl: dataset.apiMethod },
        requestData
      );
      const paramsString = getDataParamsString(
        resolvedFilters,
        resolvedSorting,
        dataset,
        undefined,
        0,
        [idItem],
        additionalFilterObject
      );
      // String transport sends the header verbatim. requestData carries null
      // when the lock was empty (templates drop the header); map it back to ""
      // so the legacy empty-header wire behavior stays unchanged.
      apiRes = await requestDatasetData(
        `${apiUrl}${paramsString}`,
        sessionToken,
        requestData.cachedQueryId ?? ''
      );
    } else {
      // requestData already carries cachedQueryId (set only when actually
      // locked — null drops the x-cachedquery-id header)
      apiRes = await apiRequest(dataset.apiMethod, requestData, (apiMethod) =>
        detailedApiMethodFetcher<any>(apiMethod, undefined, undefined, true)
      );
    }
    const ids = Array.isArray(apiRes?.data) ? apiRes.data : [apiRes?.data];
    const cachedQueryId = apiRes?.headers?.[X_CACHEDQUERY_ID] || undefined;
    return { ids, cachedQueryId };
  } catch {
    return null;
  }
};
